import React from "react";
const hitTest = (target) => {
    if (!target)
        return;
    const cell = target.closest("[data-item-index]");
    if (!cell)
        return;
    const key = cell.dataset.itemIndex;
    if (!key)
        return;
    return parseInt(key, 10);
};
export function useListCursor(props) {
    const { containerElement, hasCursor, headerItemCount, footerItemCount, itemCount, scrollToCursor, onSelectedIndexChanged, pageSize } = props;
    const [cursor, setCursor] = React.useState(() => {
        return hasCursor ? headerItemCount : -1;
    });
    const moveCursor = React.useCallback((target) => {
        const newCursor = hitTest(target);
        if (newCursor === undefined)
            return;
        if (newCursor < headerItemCount)
            return;
        if (newCursor >= itemCount - footerItemCount)
            return;
        if (newCursor === cursor)
            return;
        onSelectedIndexChanged?.(newCursor, newCursor - headerItemCount);
        setCursor(newCursor);
    }, [cursor, footerItemCount, headerItemCount, itemCount, onSelectedIndexChanged]);
    const onContainerClick = React.useCallback((e) => {
        if (!hasCursor)
            return;
        if (!containerElement)
            return;
        const srcElement = e.target;
        if (!srcElement)
            return;
        if (containerElement !== srcElement) {
            moveCursor(srcElement);
        }
        else {
            throw new Error("Code is not supposed to come to this place. Find out the use case for this condition and fix the code.");
        }
    }, [hasCursor, containerElement, moveCursor]);
    const onKeyDown = React.useCallback((e) => {
        if (!hasCursor)
            return;
        if (!containerElement)
            return;
        const minItemIndex = headerItemCount;
        const maxItemIndex = itemCount - footerItemCount - 1;
        if (cursor === headerItemCount && (e.key === "ArrowUp" || e.key === "PageUp" || e.key === "Home"))
            return;
        if (cursor === maxItemIndex && (e.key === "ArrowDown" || e.key === "PageDown" || e.key === "End"))
            return;
        let itemIndex = cursor;
        if (e.key === "ArrowDown")
            itemIndex = itemIndex + 1;
        else if (e.key === "ArrowUp")
            itemIndex = itemIndex - 1;
        else if (e.key === "PageDown")
            itemIndex = itemIndex + pageSize;
        else if (e.key === "PageUp")
            itemIndex = itemIndex - pageSize;
        else if (e.key === "Home")
            itemIndex = minItemIndex;
        else if (e.key === "End")
            itemIndex = maxItemIndex;
        if (itemIndex < minItemIndex)
            itemIndex = minItemIndex;
        if (itemIndex > maxItemIndex)
            itemIndex = maxItemIndex;
        if (itemIndex !== cursor) {
            e.preventDefault();
            setCursor(itemIndex);
            scrollToCursor(itemIndex);
            onSelectedIndexChanged?.(itemIndex, itemIndex - headerItemCount);
        }
    }, [hasCursor, containerElement, headerItemCount, itemCount, footerItemCount, cursor, pageSize, scrollToCursor, onSelectedIndexChanged]);
    React.useEffect(() => {
        if (!containerElement)
            return;
        if (!hasCursor)
            return;
        containerElement.addEventListener("click", onContainerClick);
        containerElement.addEventListener("keydown", onKeyDown);
        return () => {
            containerElement.removeEventListener("click", onContainerClick);
            containerElement.removeEventListener("keydown", onKeyDown);
        };
    }, [onContainerClick, onKeyDown, containerElement, hasCursor]);
    return [cursor, setCursor];
}
